import React, { useEffect, useState, Component } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { XIcon, WandSparklesIcon, FileIcon, CalendarCheckIcon, FlaskConicalIcon, ShieldCheckIcon, MessageSquareHeartIcon, TableIcon, BarChart3Icon, PieChartIcon, ChevronLeftIcon, ChevronRightIcon, CheckIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
import { ReportConfig } from '../../data/reports';
import { guideTopics, GuideTopic, GuideRecipe } from '../../data/builderGuide';
import { useApp } from '../../context/AppContext';
interface BuilderStartWizardProps {
  open: boolean;
  onClose: () => void;
  onStartBlank: () => void;
  onComplete: (config: ReportConfig) => void;
}
type Step = 'choice' | 'topic' | 'view';
const topicIcons: Record<string, ComponentType<{
  className?: string;
}>> = {
  appointments: CalendarCheckIcon,
  results: FlaskConicalIcon,
  clearances: ShieldCheckIcon,
  experience: MessageSquareHeartIcon
};
const chartIcons = {
  table: TableIcon,
  bar: BarChart3Icon,
  line: BarChart3Icon,
  pie: PieChartIcon
};
const stepMotion = {
  initial: {
    opacity: 0,
    x: 16
  },
  animate: {
    opacity: 1,
    x: 0
  },
  exit: {
    opacity: 0,
    x: -16
  },
  transition: {
    duration: 0.18,
    ease: 'easeOut' as const
  }
};
export function BuilderStartWizard({
  open,
  onClose,
  onStartBlank,
  onComplete
}: BuilderStartWizardProps) {
  const {
    showBuilderGuide,
    setShowBuilderGuide
  } = useApp();
  const [step, setStep] = useState<Step>('choice');
  const [topic, setTopic] = useState<GuideTopic | null>(null);
  useEffect(() => {
    if (open) {
      setStep('choice');
      setTopic(null);
    }
  }, [open]);
  const pickTopic = (t: GuideTopic) => {
    setTopic(t);
    setStep('view');
  };
  const pickRecipe = (recipe: GuideRecipe) => {
    onComplete({
      ...recipe.config,
      columns: [...recipe.config.columns]
    });
  };
  const stepIndex = step === 'choice' ? 0 : step === 'topic' ? 1 : 2;
  return <AnimatePresence data-id="element-19">
      {open && <>
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.15
      }} className="fixed inset-0 z-40 bg-navy-950/40 backdrop-blur-[2px]" onClick={onClose} aria-hidden="true" data-id="element-20" />
          <div className="pointer-events-none fixed inset-0 z-50 flex items-start justify-center overflow-y-auto p-4 sm:items-center" data-id="element-21">
            <motion.div initial={{
          opacity: 0,
          scale: 0.97,
          y: 10
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.97,
          y: 10
        }} transition={{
          duration: 0.18
        }} role="dialog" aria-modal="true" aria-labelledby="builder-start-title" className="card-float-lg pointer-events-auto my-auto w-full max-w-xl overflow-hidden rounded-2xl bg-white" data-id="element-22">
              <div className="flex items-start justify-between border-b border-navy-50 px-5 py-4" data-id="element-23">
                <div data-id="element-24">
                  <h2 id="builder-start-title" className="text-sm font-semibold text-navy-900" data-id="element-25">
                    {step === 'choice' && 'Create a Custom Report'}
                    {step === 'topic' && 'What kind of information are you looking for?'}
                    {step === 'view' && 'How would you like to see it?'}
                  </h2>
                  <p className="mt-0.5 text-xs text-navy-400" data-id="element-26">
                    {step === 'choice' && 'Pick how you’d like to get started.'}
                    {step === 'topic' && 'We’ll pre-select the most popular fields for you.'}
                    {step === 'view' && topic && `Popular ways to look at ${topic.label.toLowerCase()}.`}
                  </p>
                </div>
                <button type="button" onClick={onClose} aria-label="Close dialog" className="-mr-1 flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-navy-400 transition-colors hover:bg-navy-50 hover:text-navy-900" data-id="element-27">
                  <XIcon className="h-4 w-4" data-id="element-28" />
                </button>
              </div>

              <div className="px-5 py-5" data-id="element-29">
                <AnimatePresence mode="wait" initial={false} data-id="element-30">
                  {step === 'choice' && <motion.div key="choice" {...stepMotion} className="grid grid-cols-1 gap-3 sm:grid-cols-2" data-id="element-31">
                      <button type="button" onClick={() => setStep('topic')} className="group flex flex-col rounded-2xl border border-navy-100 p-5 text-left transition-all hover:border-navy-900 hover:card-float" data-id="element-32">
                        <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-navy-900 text-gold-300" data-id="element-33">
                          <WandSparklesIcon className="h-5 w-5" data-id="element-34" />
                        </span>
                        <span className="mt-3 text-sm font-semibold text-navy-900" data-id="element-35">
                          Build it with me
                        </span>
                        <span className="mt-1 flex-1 text-xs leading-relaxed text-navy-400" data-id="element-36">
                          Answer a couple of quick questions and we’ll set up a
                          report for you. You can fine-tune everything after.
                        </span>
                        <span className="mt-3 inline-flex items-center gap-1 text-xs font-medium text-accent-700 transition-all group-hover:gap-2" data-id="element-37">
                          Let’s go <ChevronRightIcon className="h-3.5 w-3.5" data-id="element-38" />
                        </span>
                      </button>
                      <button type="button" onClick={onStartBlank} className="group flex flex-col rounded-2xl border border-navy-100 p-5 text-left transition-all hover:border-navy-900 hover:card-float" data-id="element-39">
                        <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-navy-50 text-navy-700" data-id="element-40">
                          <FileIcon className="h-5 w-5" data-id="element-41" />
                        </span>
                        <span className="mt-3 text-sm font-semibold text-navy-900" data-id="element-42">
                          Start from scratch
                        </span>
                        <span className="mt-1 flex-1 text-xs leading-relaxed text-navy-400" data-id="element-43">
                          A blank report with every option open. Best if you
                          already know exactly what you need.
                        </span>
                        <span className="mt-3 inline-flex items-center gap-1 text-xs font-medium text-accent-700 transition-all group-hover:gap-2" data-id="element-44">
                          Open builder{' '}
                          <ChevronRightIcon className="h-3.5 w-3.5" data-id="element-45" />
                        </span>
                      </button>
                    </motion.div>}

                  {step === 'topic' && <motion.div key="topic" {...stepMotion} className="grid grid-cols-1 gap-2.5 sm:grid-cols-2" data-id="element-46">
                      {guideTopics.map(t => {
                  const Icon = topicIcons[t.id] ?? CalendarCheckIcon;
                  return <button key={t.id} type="button" onClick={() => pickTopic(t)} className="group flex flex-col rounded-2xl border border-navy-100 p-4 text-left transition-all hover:border-navy-900 hover:card-float" data-id="element-47">
                            <div className="flex items-center gap-2.5" data-id="element-48">
                              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-navy-50 text-navy-700 transition-colors group-hover:bg-navy-900 group-hover:text-gold-300" data-id="element-49">
                                <Icon className="h-4 w-4" data-id="element-50" />
                              </span>
                              <span className="text-sm font-semibold text-navy-900" data-id="element-51">
                                {t.label}
                              </span>
                            </div>
                            <span className="mt-2 flex-1 text-xs leading-relaxed text-navy-400" data-id="element-52">
                              {t.description}
                            </span>
                            <span className="mt-2.5 text-[11px] text-navy-300" data-id="element-53">
                              Popular fields: {t.popularFields.join(', ')}
                            </span>
                          </button>;
                })}
                    </motion.div>}

                  {step === 'view' && topic && <motion.div key="view" {...stepMotion} className="space-y-2.5" data-id="element-54">
                      {topic.recipes.map(recipe => {
                  const Icon = chartIcons[recipe.config.chartType];
                  return <button key={recipe.id} type="button" onClick={() => pickRecipe(recipe)} className="group flex w-full items-center gap-3.5 rounded-2xl border border-navy-100 p-4 text-left transition-all hover:border-navy-900 hover:card-float" data-id="element-55">
                            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-navy-50 text-navy-700 transition-colors group-hover:bg-navy-900 group-hover:text-gold-300" data-id="element-56">
                              <Icon className="h-4 w-4" data-id="element-57" />
                            </span>
                            <span className="min-w-0 flex-1" data-id="element-58">
                              <span className="block text-sm font-semibold text-navy-900" data-id="element-59">
                                {recipe.label}
                              </span>
                              <span className="mt-0.5 block text-xs leading-relaxed text-navy-400" data-id="element-60">
                                {recipe.description}
                              </span>
                            </span>
                            <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-navy-50 text-navy-400 transition-colors group-hover:bg-accent-50 group-hover:text-accent-700" data-id="element-61">
                              <CheckIcon className="h-3.5 w-3.5" data-id="element-62" />
                            </span>
                          </button>;
                })}
                    </motion.div>}
                </AnimatePresence>
              </div>

              <div className="flex items-center justify-between border-t border-navy-50 px-5 py-3.5" data-id="element-63">
                <div className="flex items-center gap-1.5" aria-hidden="true" data-id="element-64">
                  {[0, 1, 2].map(i => <span key={i} className={`h-1.5 rounded-full transition-all ${i === stepIndex ? 'w-5 bg-navy-900' : i < stepIndex ? 'w-1.5 bg-navy-300' : 'w-1.5 bg-navy-100'}`} data-id="element-65" />)}
                </div>
                {step === 'choice' ? <label className="flex cursor-pointer items-center gap-2 text-xs text-navy-500" data-id="element-66">
                    <input type="checkbox" checked={!showBuilderGuide} onChange={e => setShowBuilderGuide(!e.target.checked)} className="h-3.5 w-3.5 rounded border-navy-200 accent-[#102a43]" data-id="element-67" />
                    <span data-id="element-68">
                      Hide this — go straight to a blank report.{' '}
                      <Link to="/settings" className="text-accent-700 hover:underline" onClick={onClose} data-id="element-69">
                        Change in Settings
                      </Link>
                    </span>
                  </label> : <button type="button" onClick={() => setStep(step === 'view' ? 'topic' : 'choice')} className="inline-flex items-center gap-1 text-xs font-medium text-navy-500 transition-colors hover:text-navy-900" data-id="element-70">
                    <ChevronLeftIcon className="h-3.5 w-3.5" data-id="element-71" /> Back
                  </button>}
              </div>
            </motion.div>
          </div>
        </>}
    </AnimatePresence>;
}